import api from "../services/api";

/**
 * Erro lançado quando a busca de campos é chamada sem um formulário válido.
 * Serve para o chamador diferenciar "id inválido" de "erro de rede/API".
 */
export class FormularioIdInvalidoError extends Error {
  constructor(formularioId) {
    super(`formulario_id inválido: ${JSON.stringify(formularioId)}`);
    this.name = "FormularioIdInvalidoError";
    this.formularioId = formularioId;
  }
}

/**
 * Valida o id do formulário antes de qualquer chamada à API.
 *
 * @param {*} formularioId - Id a ser validado
 * @returns {boolean} - true quando é um inteiro positivo
 */
export function isFormularioIdValido(formularioId) {
  if (formularioId === null || formularioId === undefined) {
    return false;
  }

  const id = Number(formularioId);

  return Number.isInteger(id) && id > 0;
}

/**
 * Busca os campos de um formulário.
 *
 * O endpoint /formularios/campos/ ignora o filtro quando `formulario_id` chega
 * vazio e devolve campos de todos os formulários da base - de todos os clientes.
 * Por isso a requisição só sai com um id válido e a resposta ainda é filtrada
 * pelo formulário pedido, como segunda barreira.
 *
 * @param {*} formularioId - Id do formulário
 * @param {Object} [opcoes]
 * @param {number|string|null} [opcoes.status=1] - Filtro de status (null não envia)
 * @param {number|null} [opcoes.limit=500] - Limite de registros (null não envia)
 * @param {Object} [opcoes.config] - Config extra do axios (headers, etc)
 * @param {boolean} [opcoes.exibicao=true] - Textos no idioma de quem lê (nome, descrição e
 *   rótulos das opções). O cadastro do formulário passa false para editar os originais.
 * @returns {Promise<Array>} - Campos do formulário informado
 * @throws {FormularioIdInvalidoError} - Quando o id não é um inteiro positivo
 */
export async function carregarCamposFormulario(formularioId, opcoes = {}) {
  const { status = 1, limit = 500, config, exibicao = true } = opcoes;

  if (!isFormularioIdValido(formularioId)) {
    throw new FormularioIdInvalidoError(formularioId);
  }

  const id = Number(formularioId);

  const params = [`formulario_id=${id}`];
  if (status !== null && status !== undefined) {
    params.push(`status=${status}`);
  }
  if (limit !== null && limit !== undefined) {
    params.push(`limit=${limit}`);
  }

  const response = await api.get(`/formularios/campos/?${params.join("&")}`, config);
  const campos = response.data?.results || [];

  const doFormulario = campos.filter((campo) => Number(campo.formulario_id) === id);

  return exibicao ? doFormulario.map(textosParaExibicao) : doFormulario;
}

/**
 * Troca nome, descrição e opções pelos textos no idioma de quem lê, quando o backend os
 * manda. Nas opções só o rótulo muda: o valor gravado e o que as regras comparam é o mesmo.
 * O original fica em `nome_original` para quem precisar dele.
 */
export function textosParaExibicao(campo) {
  if (!campo) return campo;

  return {
    ...campo,
    nome_original: campo.nome,
    nome: campo.nome_exibicao || campo.nome,
    descricao: campo.descricao_exibicao ?? campo.descricao,
    opcoes: campo.opcoes_exibicao ?? campo.opcoes,
  };
}
